import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";

type FormFieldOption = {
  id: string;
  field_id: string;
  valor: string;
  label: string;
  ordem: number;
  parent_option_id: string | null;
  sub_opcoes?: FormFieldOption[];
};

type FormField = {
  id: string;
  form_id: string;
  nome: string;
  slug: string;
  tipo: string;
  obrigatorio: boolean;
  placeholder: string | null;
  descricao: string | null;
  ordem: number;
  permite_sub_opcoes: boolean;
  options?: FormFieldOption[];
};

type FormDependency = {
  id: string;
  field_id: string;
  depends_on_field_id: string;
  condition_value: string | null;
  condition_operator: string;
};

type OptionRelation = {
  id: string;
  option_id: string;
  related_option_id: string;
};

interface DynamicFormProps {
  formId: string;
  entityType?: "product" | "client" | "supplier" | "transaction" | "custom" | null;
  entityId?: string | null;
  customIdentifier?: string | null;
  onSubmit?: (values: Record<string, any>) => void;
  onCancel?: () => void;
  initialValues?: Record<string, any>;
  readOnly?: boolean;
}

export function DynamicForm({
  formId,
  entityType,
  entityId,
  customIdentifier,
  onSubmit,
  onCancel,
  initialValues = {},
  readOnly = false,
}: DynamicFormProps) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [formValues, setFormValues] = useState<Record<string, any>>(initialValues);

  // Carregar formulário com campos e opções
  const { data: formData, isLoading } = useQuery({
    queryKey: ["form-data", formId],
    queryFn: async () => {
      // Buscar formulário
      const { data: form, error: formError } = await supabase
        .from("forms")
        .select("*")
        .eq("id", formId)
        .single();

      if (formError) throw formError;

      // Buscar campos ordenados
      const { data: fields, error: fieldsError } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formId)
        .order("ordem", { ascending: true });

      if (fieldsError) throw fieldsError;

      // Buscar opções para cada campo
      const fieldsWithOptions = await Promise.all(
        (fields || []).map(async (field) => {
          console.log(`[DEBUG] Buscando opções para campo: ${field.nome} (${field.id}), tipo: ${field.tipo}`);
          
          // Buscar opções com tenant_id para garantir que está buscando do tenant correto
          let query = supabase
            .from("form_field_options")
            .select("*")
            .eq("field_id", field.id)
            .order("ordem", { ascending: true });

          // Se temos tenantId, filtrar por ele também (opcional, pois RLS já faz isso)
          if (tenantId) {
            query = query.eq("tenant_id", tenantId);
          }

          const { data: options, error: optionsError } = await query;

          console.log(`[DEBUG] Resultado da query para campo ${field.nome}:`, {
            data: options,
            error: optionsError,
            count: options?.length || 0,
            tenantId: tenantId,
            fieldId: field.id
          });

          if (optionsError) {
            console.error(`[ERROR] Erro ao buscar opções para campo ${field.id}:`, optionsError);
          }

          // Organizar opções hierarquicamente
          const optionsList = (options || []) as FormFieldOption[];
          console.log(`[DEBUG] Opções processadas para ${field.nome}:`, optionsList);
          
          const rootOptions = optionsList.filter((opt) => !opt.parent_option_id);
          const buildTree = (parentId: string | null): FormFieldOption[] => {
            return optionsList
              .filter((opt) => opt.parent_option_id === parentId)
              .map((opt) => ({
                ...opt,
                sub_opcoes: buildTree(opt.id),
              }));
          };

          const fieldWithOptions = {
            ...field,
            options: rootOptions.map((opt) => ({
              ...opt,
              sub_opcoes: buildTree(opt.id),
            })),
          } as FormField & { options: FormFieldOption[] };

          // Debug: log das opções carregadas
          if (field.tipo === "select" || field.tipo === "radio" || field.tipo === "multiselect" || field.tipo === "checkbox") {
            console.log(`[DEBUG] Campo ${field.nome} (${field.id}) - Resumo FINAL:`, {
              totalOptions: optionsList.length,
              rootOptions: rootOptions.length,
              options: fieldWithOptions.options,
              optionsList: optionsList,
              fieldTipo: field.tipo,
            });
            
            if (optionsList.length === 0) {
              console.error(`[ERROR] Campo ${field.nome} do tipo ${field.tipo} não tem opções cadastradas! Verifique se as opções foram salvas corretamente no banco.`);
            }
          }

          return fieldWithOptions;
        })
      );

      // Buscar dependências
      const { data: dependencies } = await supabase
        .from("form_field_dependencies")
        .select("*")
        .in(
          "field_id",
          fieldsWithOptions.map((f) => f.id)
        );

      // Buscar relacionamentos many-to-many entre opções
      // Buscar todas as opções dos campos primeiro
      const allOptionIds = fieldsWithOptions
        .flatMap((f) => {
          const flatten = (opts: FormFieldOption[]): string[] => {
            const ids: string[] = [];
            for (const opt of opts) {
              ids.push(opt.id);
              if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
                ids.push(...flatten(opt.sub_opcoes));
              }
            }
            return ids;
          };
          return f.options ? flatten(f.options) : [];
        });

      let optionRelations: any[] = [];
      if (allOptionIds.length > 0) {
        console.log("[DEBUG] DynamicForm - Buscando relacionamentos de opções", {
          allOptionIdsCount: allOptionIds.length,
          allOptionIds: allOptionIds.slice(0, 10), // Mostrar apenas os primeiros 10
        });

        const { data, error } = await supabase
          .from("form_field_option_relations")
          .select("*")
          .in("option_id", allOptionIds);
        
        // Se a tabela não existe (404), retornar array vazio sem erro
        if (error && error.code !== "PGRST116") {
          console.error("[ERROR] DynamicForm - Erro ao buscar relacionamentos de opções", {
            allOptionIdsCount: allOptionIds.length,
            error: error,
            errorCode: error.code,
            errorMessage: error.message,
            errorDetails: error.details,
            errorHint: error.hint,
          });
        } else if (error && error.code === "PGRST116") {
          console.warn("[WARNING] DynamicForm - Tabela form_field_option_relations não existe (404)", {
            allOptionIdsCount: allOptionIds.length,
          });
        } else {
          console.log("[DEBUG] DynamicForm - Relacionamentos encontrados", {
            relationsCount: data?.length || 0,
            relations: data,
            allOptionIds: allOptionIds,
          });
          
          // Se não há relacionamentos, verificar se a tabela está vazia ou se há problema na query
          if (data?.length === 0) {
            console.warn("[WARNING] DynamicForm - Nenhum relacionamento encontrado no banco. Verificando se há relacionamentos com related_option_id...");
            
            // Tentar buscar também por related_option_id para verificar se há relacionamentos invertidos
            const { data: relationsByRelated } = await supabase
              .from("form_field_option_relations")
              .select("*")
              .in("related_option_id", allOptionIds);
            
            console.log("[DEBUG] DynamicForm - Relacionamentos por related_option_id", {
              relationsCount: relationsByRelated?.length || 0,
              relations: relationsByRelated,
            });
          }
        }
        
        optionRelations = data || [];
      } else {
        console.log("[DEBUG] DynamicForm - Nenhuma opção para buscar relacionamentos");
      }

      return {
        form,
        fields: fieldsWithOptions,
        dependencies: (dependencies || []) as FormDependency[],
        optionRelations: (optionRelations || []) as Array<{
          id: string;
          option_id: string;
          related_option_id: string;
        }>,
      };
    },
  });

  // Criar schema de validação dinâmico
  const formSchema = useMemo(() => {
    if (!formData?.fields) return z.object({});

    const schemaFields: Record<string, z.ZodTypeAny> = {};

    formData.fields.forEach((field) => {
      let fieldSchema: z.ZodTypeAny;

      switch (field.tipo) {
        case "text":
        case "email":
        case "tel":
          fieldSchema = z.string();
          break;
        case "textarea":
          fieldSchema = z.string();
          break;
        case "number":
          fieldSchema = z.number().or(z.string().transform((val) => parseFloat(val) || 0));
          break;
        case "date":
        case "datetime":
          fieldSchema = z.string();
          break;
        case "select":
        case "radio":
          fieldSchema = z.string();
          break;
        case "multiselect":
        case "checkbox":
          fieldSchema = z.array(z.string()).or(z.string().transform((val) => [val]));
          break;
        default:
          fieldSchema = z.string().optional();
      }

      if (field.obrigatorio) {
        fieldSchema = fieldSchema.refine((val) => {
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === "string") return val.trim().length > 0;
          return val !== null && val !== undefined;
        }, `${field.nome} é obrigatório`);
      } else {
        fieldSchema = fieldSchema.optional();
      }

      schemaFields[field.slug] = fieldSchema;
    });

    return z.object(schemaFields);
  }, [formData?.fields]);

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<any>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  // Atualizar valores quando initialValues mudar (apenas uma vez na montagem)
  const initialValuesRef = useRef(initialValues);
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0 && initialValuesRef.current !== initialValues) {
      initialValuesRef.current = initialValues;
      form.reset(initialValues);
      setFormValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Função auxiliar para achatar opções hierárquicas em uma lista plana
  const flattenOptions = (options: FormFieldOption[]): FormFieldOption[] => {
    if (!options || options.length === 0) return [];
    
    const result: FormFieldOption[] = [];
    
    const flatten = (opts: FormFieldOption[]) => {
      for (const opt of opts) {
        // Criar uma cópia sem sub_opcoes para evitar problemas
        const { sub_opcoes, ...optWithoutSubs } = opt;
        result.push(optWithoutSubs as FormFieldOption);
        if (sub_opcoes && sub_opcoes.length > 0) {
          flatten(sub_opcoes);
        }
      }
    };
    
    flatten(options);
    console.log(`[DEBUG] flattenOptions: ${options.length} opções originais → ${result.length} opções achatadas`, result);
    return result;
  };

  // Filtrar opções baseado em dependências e relacionamentos many-to-many
  const getFilteredOptions = (field: FormField & { options?: FormFieldOption[] }) => {
    console.log(`[DEBUG] getFilteredOptions para campo ${field.nome}:`, {
      temOpcoes: !!field.options,
      quantidadeOpcoes: field.options?.length || 0,
      opcoes: field.options,
    });

    if (!field.options || field.options.length === 0) {
      console.warn(`[WARNING] Campo ${field.nome} não tem opções!`);
      return [];
    }

    const dependency = formData?.dependencies.find((d) => d.field_id === field.id);
    console.log(`[DEBUG] Dependência para campo ${field.nome}:`, dependency);
    
    // Se não há dependência, retornar todas as opções (achatadas se necessário)
    if (!dependency) {
      const flattened = flattenOptions(field.options);
      console.log(`[DEBUG] Campo ${field.nome} sem dependência, retornando ${flattened.length} opções achatadas`);
      return flattened;
    }

    const dependentField = formData.fields.find((f) => f.id === dependency.depends_on_field_id);
    if (!dependentField) {
      return flattenOptions(field.options);
    }

    const dependentFieldValue = form.watch(dependentField.slug) as any;
    if (!dependentFieldValue) {
      // Se não há valor selecionado no campo dependente, retornar vazio
      return [];
    }

    // Buscar a opção selecionada no campo dependente (pai)
    const dependentFieldOptionsFlat = flattenOptions(dependentField.options || []);
    const selectedParentOption = dependentFieldOptionsFlat.find(
      (opt) => opt.valor === dependentFieldValue
    );

    if (!selectedParentOption) {
      console.warn(`[WARNING] Opção selecionada "${dependentFieldValue}" não encontrada no campo dependente`);
      return [];
    }

    // Buscar relacionamentos many-to-many
    // Opções do campo atual que estão relacionadas com a opção selecionada do campo pai
    const optionRelations = (formData as any)?.optionRelations || [];
    
    console.log(`[DEBUG] getFilteredOptions - Analisando relacionamentos:`, {
      selectedParentOptionId: selectedParentOption.id,
      selectedParentOptionValor: selectedParentOption.valor,
      selectedParentOptionLabel: selectedParentOption.label,
      totalRelations: optionRelations.length,
      allRelations: optionRelations,
      fieldId: field.id,
      fieldNome: field.nome,
    });
    
    const relatedOptionIds = optionRelations
      .filter((rel: OptionRelation) => rel.related_option_id === selectedParentOption.id)
      .map((rel: OptionRelation) => rel.option_id);

    console.log(`[DEBUG] Relacionamentos encontrados:`, {
      selectedParentOption: selectedParentOption.id,
      relatedOptionIds,
      totalRelations: optionRelations.length,
      filteredRelations: optionRelations.filter((rel: OptionRelation) => rel.related_option_id === selectedParentOption.id),
    });

    // Se há relacionamentos many-to-many, filtrar opções baseado neles
    if (relatedOptionIds.length > 0) {
      const allOptionsFlat = flattenOptions(field.options);
      const filtered = allOptionsFlat.filter((opt) => relatedOptionIds.includes(opt.id));
      console.log(`[DEBUG] Campo ${field.nome} com relacionamentos many-to-many: ${filtered.length} opções filtradas`);
      return filtered;
    }

    // Fallback: usar lógica hierárquica antiga (sub-opções)
    // Buscar a opção selecionada na estrutura hierárquica original do campo dependente
    const dependentFieldOptionsHierarchical = dependentField.options || [];
    const findOptionInTree = (opts: FormFieldOption[], valor: string): FormFieldOption | null => {
      for (const opt of opts) {
        if (opt.valor === valor) {
          return opt;
        }
        if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
          const found = findOptionInTree(opt.sub_opcoes, valor);
          if (found) return found;
        }
      }
      return null;
    };
    
    const selectedOption = findOptionInTree(dependentFieldOptionsHierarchical, dependentFieldValue as any);
    
    if (selectedOption && selectedOption.sub_opcoes && selectedOption.sub_opcoes.length > 0) {
      // O valor selecionado é uma opção pai, mostrar apenas as sub-opções correspondentes
      console.log(`[DEBUG] Campo ${field.nome} usando lógica hierárquica: ${selectedOption.sub_opcoes.length} sub-opções encontradas`);
      return selectedOption.sub_opcoes;
    }

    // Se não há relacionamentos nem sub-opções, retornar vazio
    console.log(`[WARNING] Nenhum relacionamento encontrado para opção ${selectedParentOption.id} e nenhuma sub-opção hierárquica`);
    return [];
  };

  const submitMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");

      // Criar submissão
      const { data: submission, error: submissionError } = await supabase
        .from("form_submissions")
        .insert({
          form_id: formId,
          entity_type: entityType || null,
          entity_id: entityId || null,
          custom_identifier: customIdentifier || null,
          status: "pendente",
          tenant_id: tenantId,
        })
        .select()
        .single();

      if (submissionError) throw submissionError;

      // Salvar valores dos campos
      if (formData?.fields && submission) {
        const valuesToInsert = formData.fields.map((field) => {
          const value = values[field.slug];
          const isArray = Array.isArray(value);

          return {
            submission_id: submission.id,
            field_id: field.id,
            valor: isArray ? JSON.stringify(value) : String(value || ""),
            valor_json: isArray ? value : null,
            tenant_id: tenantId,
          };
        });

        const { error: valuesError } = await supabase
          .from("form_submission_values")
          .insert(valuesToInsert);

        if (valuesError) throw valuesError;
      }

      return submission;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-submissions", formId] });
      toast.success("Formulário enviado com sucesso");
      onSubmit?.(formValues);
    },
    onError: (error: Error) => {
      toast.error("Erro ao enviar formulário: " + error.message);
    },
  });

  const handleSubmit = (values: FormValues) => {
    setFormValues(values);
    if (onSubmit) {
      onSubmit(values);
    } else {
      submitMutation.mutate(values);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!formData) {
    return <div>Formulário não encontrado</div>;
  }

  const sortedFields = [...formData.fields].sort((a, b) => a.ordem - b.ordem);

  return (
    <Form {...form}>
      <form 
        onSubmit={form.handleSubmit(handleSubmit)} 
        className="space-y-6"
        onClick={(e) => {
          // Prevenir propagação de eventos de clique no formulário
          if (readOnly) {
            e.stopPropagation();
          }
        }}
        onMouseDown={(e) => {
          // Prevenir propagação de eventos de mouse no formulário
          if (readOnly) {
            e.stopPropagation();
          }
        }}
      >
        {formData.form.descricao && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">{formData.form.descricao}</p>
            </CardContent>
          </Card>
        )}

        {sortedFields.map((field) => {
          const filteredOptions = getFilteredOptions(field);
          const fieldValue = form.watch(field.slug) as any;

          // Debug: log das opções filtradas para campos select
          if (field.tipo === "select" || field.tipo === "radio" || field.tipo === "multiselect" || field.tipo === "checkbox") {
            console.log(`[DEBUG] Renderizando campo ${field.nome}:`, {
              tipo: field.tipo,
              temOpcoesOriginais: !!field.options,
              quantidadeOpcoesOriginais: field.options?.length || 0,
              quantidadeOpcoesFiltradas: filteredOptions.length,
              opcoesOriginais: field.options,
              opcoesFiltradas: filteredOptions,
              fieldId: field.id,
            });
            
            if (filteredOptions.length === 0) {
              // Verificar se o campo tem dependência e se o campo dependente não foi selecionado
              const dependency = formData?.dependencies.find((d) => d.field_id === field.id);
              const dependentField = dependency ? formData.fields.find((f) => f.id === dependency.depends_on_field_id) : null;
              const dependentFieldValue = dependentField ? form.watch(dependentField.slug) as any : null;
              
              if (field.options && field.options.length > 0) {
                // Se tem dependência e o campo dependente não foi selecionado, é esperado que não haja opções
                if (dependency && !dependentFieldValue) {
                  console.log(`[DEBUG] Campo ${field.nome}: Sem opções porque depende de ${dependentField?.nome || 'campo dependente'} que ainda não foi selecionado`, {
                    fieldId: field.id,
                    dependencyFieldId: dependency.depends_on_field_id,
                    dependentFieldValue,
                  });
                } else {
                  // Caso contrário, é um erro real
                  console.error(`[ERROR] Campo ${field.nome}: Opções originais existem mas filtradas estão vazias!`, {
                    originais: field.options,
                    filtradas: filteredOptions,
                    fieldId: field.id,
                    hasDependency: !!dependency,
                    dependentFieldValue,
                  });
                }
              } else {
                console.warn(`[WARNING] Campo ${field.nome}: Não há opções cadastradas para este campo select/radio/checkbox!`, {
                  fieldId: field.id,
                  tipo: field.tipo,
                });
              }
            }
          }

          return (
            <FormField
              key={field.id}
              control={form.control}
              name={field.slug as any}
              render={({ field: formField }) => (
                <FormItem>
                  <FormLabel>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive ml-1">*</span>}
                  </FormLabel>
                  <FormControl>
                    {(() => {
                      switch (field.tipo) {
                        case "text":
                          return (
                            <Input
                              {...formField}
                              placeholder={field.placeholder || ""}
                              disabled={readOnly}
                            />
                          );
                        case "textarea":
                          return (
                            <Textarea
                              {...formField}
                              placeholder={field.placeholder || ""}
                              disabled={readOnly}
                            />
                          );
                        case "number":
                          return (
                            <Input
                              {...formField}
                              type="number"
                              placeholder={field.placeholder || ""}
                              disabled={readOnly}
                              onChange={(e) => formField.onChange(parseFloat(e.target.value) || 0)}
                            />
                          );
                        case "email":
                          return (
                            <Input
                              {...formField}
                              type="email"
                              placeholder={field.placeholder || ""}
                              disabled={readOnly}
                            />
                          );
                        case "tel":
                          return (
                            <Input
                              {...formField}
                              type="tel"
                              placeholder={field.placeholder || ""}
                              disabled={readOnly}
                            />
                          );
                        case "date":
                          return (
                            <Input
                              {...formField}
                              type="date"
                              disabled={readOnly}
                            />
                          );
                        case "datetime":
                          return (
                            <Input
                              {...formField}
                              type="datetime-local"
                              disabled={readOnly}
                            />
                          );
                        case "select":
                          console.log(`[DEBUG] Renderizando SELECT para campo ${field.nome}:`, {
                            filteredOptionsCount: filteredOptions.length,
                            filteredOptions: filteredOptions,
                            fieldOptions: field.options,
                            readOnly: readOnly,
                          });
                          
                          return (
                            <Select
                              onValueChange={formField.onChange}
                              value={formField.value || ""}
                              disabled={false}
                              onOpenChange={(open) => {
                                // Prevenir propagação quando o Select abre/fecha
                                if (readOnly) {
                                  // Não fazer nada especial, apenas evitar propagação
                                }
                              }}
                            >
                              <SelectTrigger
                                onClick={(e) => {
                                  if (readOnly) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                <SelectValue placeholder={field.placeholder || "Selecione..."} />
                              </SelectTrigger>
                              <SelectContent
                                onClick={(e) => {
                                  // Prevenir propagação de cliques dentro do SelectContent
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  // Prevenir propagação de eventos de mouse
                                  e.stopPropagation();
                                }}
                              >
                                {filteredOptions.length > 0 ? (
                                  filteredOptions.map((option) => (
                                    <SelectItem 
                                      key={option.id} 
                                      value={option.valor}
                                      onClick={(e) => {
                                        // Prevenir propagação ao clicar em um item
                                        e.stopPropagation();
                                      }}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                    {field.options && field.options.length > 0 
                                      ? "Nenhuma opção disponível (filtro aplicado)" 
                                      : "Nenhuma opção cadastrada"}
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          );
                        case "radio":
                          return (
                            <RadioGroup
                              onValueChange={formField.onChange}
                              value={formField.value}
                              disabled={readOnly}
                            >
                              {filteredOptions.map((option) => (
                                <div key={option.id} className="flex items-center space-x-2">
                                  <RadioGroupItem value={option.valor} id={option.id} />
                                  <Label htmlFor={option.id}>{option.label}</Label>
                                </div>
                              ))}
                            </RadioGroup>
                          );
                        case "multiselect":
                          return (
                            <Select
                              onValueChange={(value) => {
                                const current = Array.isArray(formField.value) ? formField.value : [];
                                if (current.includes(value)) {
                                  formField.onChange(current.filter((v) => v !== value));
                                } else {
                                  formField.onChange([...current, value]);
                                }
                              }}
                              disabled={false}
                              onOpenChange={(open) => {
                                // Prevenir propagação quando o Select abre/fecha
                                if (readOnly) {
                                  // Não fazer nada especial, apenas evitar propagação
                                }
                              }}
                            >
                              <SelectTrigger
                                onClick={(e) => {
                                  if (readOnly) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                <SelectValue placeholder={field.placeholder || "Selecione..."} />
                              </SelectTrigger>
                              <SelectContent
                                onClick={(e) => {
                                  // Prevenir propagação de cliques dentro do SelectContent
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  // Prevenir propagação de eventos de mouse
                                  e.stopPropagation();
                                }}
                              >
                                {filteredOptions.length > 0 ? (
                                  filteredOptions.map((option) => (
                                    <SelectItem 
                                      key={option.id} 
                                      value={option.valor}
                                      onClick={(e) => {
                                        // Prevenir propagação ao clicar em um item
                                        e.stopPropagation();
                                      }}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                    {field.options && field.options.length > 0 
                                      ? "Nenhuma opção disponível (filtro aplicado)" 
                                      : "Nenhuma opção cadastrada"}
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          );
                        case "checkbox":
                          return (
                            <div className="space-y-2">
                              {filteredOptions.map((option) => (
                                <div key={option.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    checked={
                                      Array.isArray(formField.value)
                                        ? (formField.value as any[]).includes(option.valor)
                                        : false
                                    }
                                    onCheckedChange={(checked) => {
                                      const current = Array.isArray(formField.value)
                                        ? formField.value
                                        : [];
                                      if (checked) {
                                        formField.onChange([...current, option.valor]);
                                      } else {
                                        formField.onChange(current.filter((v) => v !== option.valor));
                                      }
                                    }}
                                    disabled={readOnly}
                                  />
                                  <Label>{option.label}</Label>
                                </div>
                              ))}
                            </div>
                          );
                        case "file":
                          return (
                            <Input
                              {...formField}
                              type="file"
                              disabled={readOnly}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  formField.onChange(file.name);
                                }
                              }}
                            />
                          );
                        default:
                          return (
                            <Input
                              {...formField}
                              placeholder={field.placeholder || ""}
                              disabled={readOnly}
                            />
                          );
                      }
                    })()}
                  </FormControl>
                  {field.descricao && (
                    <FormDescription>{field.descricao}</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        })}

        {!readOnly && (
          <div className="flex justify-end gap-2">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancelar
              </Button>
            )}
            <Button type="submit" disabled={submitMutation.isPending}>
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar"
              )}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}

